import React, { useMemo } from "react";
import { Box, Button, Stack, Text, useColorModeValue } from "@chakra-ui/react";
import { Wizard } from "react-use-wizard";
import { PanelStep } from "screens/panels/components/PanelStep";
import { PanelView } from "screens/panels/components/PanelView";
import { useButtonProps } from "hooks";
import { DynamicForm } from "screens/common/components";
import type { Ticker } from "api/tickers/models/Ticker";
import { formatDate } from "screens/common/modal/formatters";
import { useTickerTypeOptions } from "../utils/tickerTypesOptions";
import { useExchangeOptions } from "../utils/exchangeOptions";
import { TickerChangeLog } from "./TickerChangeLog";
import { usePricingTierOptions } from "../utils/pricingTierOptions";
import { getTickerFormFields } from "../utils/tickerFormFields";
import { formatAliasesForDisplay } from "../utils/aliasUtils";

export interface UpdateTickerForm {
  symbol: string;
  name: string;
  dataProviderId: string | "null";
  exchange: string;
  status: "active" | "inactive" | "pending" | "deleted";
  type: string | "null";
  notes: string;
  companyLogoUrl: string;
  companyBannerUrl: string;
  companyUrl: string;
  companyTaxId: string;
  companyLegalName: string;
  pricingTier: string;
  investmentScoreAnalytical: number | null;
  investmentScoreSentiment: number | null;
  overallInvestmentScore: number | null;
  marketCap: number | null;
  fairValue: number | null;
  sellRating: any | null;
  revenue: number | null;
  revenueGrowthRate: number | null;
  country: string | null;
  stateOrProvince: string | null;
  city: string | null;
  address: string | null;
  yearFounded: number | null;
  industries: string[];
  companyDescription: string | null;
  companyType: string | null;
  companyLegalStructure: string | null;
  numberOfEmployees: number | null;
  ipoStatus: string | null;
  companyTradingStatus: string | null;
  companyStockExchangeStatus: string | null;
  companyCreditScore: string | null;
  raisingCapital: string | null;
  raisingCapitalAmount: string | null;
  companyLinkedinUrl: string | null;
  companyCrunchbaseUrl: string | null;
  dataProviderConfidenceScore: string | null;
  inferredCompanyStage: string | null;
  inferredIndustries: string[];
  inferredGrowthRate: number | null;
  inferredRevenue: number | null;
  aliases: string;
}

interface IProps {
  onSubmit: (values: UpdateTickerForm, id?: string) => void;
  isLoading?: boolean;
  ticker?: Ticker;
}

interface IPanelProps extends IProps {
  onClose: () => void;
  isOpen: boolean;
}

const UpdateTicker = ({ onSubmit, ticker, isLoading }: IProps) => {
  const textColor = useColorModeValue("primary.darkGray", "gray.400");

  const pricingTiers = usePricingTierOptions();
  const tickerTypeOptions = useTickerTypeOptions();
  const exchangeOptions = useExchangeOptions();

  const fields = useMemo(
    () =>
      getTickerFormFields({
        exchangeOptions,
        tickerTypeOptions,
        pricingTiers,
        statusOptions: [
          { value: "active", label: "Active" },
          { value: "inactive", label: "Inactive" },
          { value: "pending", label: "Pending" },
          { value: "deleted", label: "Deleted" },
        ],
      }),
    [exchangeOptions, tickerTypeOptions, pricingTiers]
  );

  const defaultValues: UpdateTickerForm = useMemo(() => {
    return ticker
      ? {
          symbol: ticker.symbol,
          exchange: ticker.exchange,
          dataProviderId: ticker.dataProviderId ?? "",
          name: ticker.name,
          status: ticker.status,
          type: ticker.type ?? "null",
          notes: ticker.notes ?? "",
          companyLogoUrl: ticker.companyLogoUrl ?? "",
          companyBannerUrl: ticker.companyBannerUrl ?? "",
          companyUrl: ticker.companyUrl ?? "",
          companyTaxId: ticker.companyTaxId ?? "",
          companyLegalName: ticker.companyLegalName ?? "",
          pricingTier: ticker.pricingTier ?? "",
          investmentScoreAnalytical: ticker.investmentScoreAnalytical ?? null,
          investmentScoreSentiment: ticker.investmentScoreSentiment ?? null,
          overallInvestmentScore: ticker.overallInvestmentScore ?? null,
          marketCap: ticker.marketCap ?? null,
          fairValue: ticker.fairValue ?? null,
          sellRating: ticker.sellRating ?? null,
          revenue: ticker.revenue ?? null,
          revenueGrowthRate: ticker.revenueGrowthRate ?? null,
          country: ticker.country ?? "",
          stateOrProvince: ticker.stateOrProvince ?? "",
          city: ticker.city ?? "",
          address: ticker.address ?? "",
          yearFounded: ticker.yearFounded ?? null,
          industries: ticker.industries ?? [],
          companyDescription: ticker.companyDescription ?? "",
          companyType: ticker.companyType ?? "",
          companyLegalStructure: ticker.companyLegalStructure ?? "",
          numberOfEmployees: ticker.numberOfEmployees ?? null,
          ipoStatus: ticker.ipoStatus ?? "",
          companyTradingStatus: ticker.companyTradingStatus ?? "",
          companyStockExchangeStatus: ticker.companyStockExchangeStatus ?? "",
          companyCreditScore: ticker.companyCreditScore ?? "",
          raisingCapital: ticker.raisingCapital ?? "",
          raisingCapitalAmount: ticker.raisingCapitalAmount ?? "",
          companyLinkedinUrl: ticker.companyLinkedinUrl ?? "",
          companyCrunchbaseUrl: ticker.companyCrunchbaseUrl ?? "",
          dataProviderConfidenceScore: ticker.dataProviderConfidenceScore ?? "",
          inferredCompanyStage: ticker.inferredCompanyStage ?? "",
          inferredIndustries: ticker.inferredIndustries ?? [],
          inferredGrowthRate: ticker.inferredGrowthRate ?? null,
          inferredRevenue: ticker.inferredRevenue ?? null,
          aliases: formatAliasesForDisplay(ticker.aliases),
        }
      : {
          symbol: "",
          exchange: "",
          dataProviderId: "",
          name: "",
          status: "active",
          type: "null",
          notes: "",
          companyLogoUrl: "",
          companyBannerUrl: "",
          companyUrl: "",
          companyTaxId: "",
          companyLegalName: "",
          pricingTier: "",
          investmentScoreAnalytical: null,
          investmentScoreSentiment: null,
          overallInvestmentScore: null,
          marketCap: null,
          fairValue: null,
          sellRating: null,
          revenue: null,
          revenueGrowthRate: null,
          country: "",
          stateOrProvince: "",
          city: "",
          address: "",
          yearFounded: null,
          industries: [],
          companyDescription: "",
          companyType: "",
          companyLegalStructure: "",
          numberOfEmployees: null,
          ipoStatus: "",
          companyTradingStatus: "",
          companyStockExchangeStatus: "",
          companyCreditScore: "",
          raisingCapital: "",
          raisingCapitalAmount: "",
          companyLinkedinUrl: "",
          companyCrunchbaseUrl: "",
          dataProviderConfidenceScore: "",
          inferredCompanyStage: "",
          inferredIndustries: [],
          inferredGrowthRate: null,
          inferredRevenue: null,
          aliases: "",
        };
  }, [ticker]);

  return (
    <Stack spacing="1rem">
      {ticker && (
        <Stack direction="row" spacing="2rem" width="100%" justifyContent={"space-between"} align="center">
          <Text wordBreak="break-word" fontWeight="light" fontSize="sm">{`Created ${formatDate(new Date(ticker.creationDate))} by ${
            ticker?.creationByUser
          }`}</Text>
          {ticker.lastUpdatedDate && ticker.lastUpdatedByUser && (
            <Text wordBreak="break-word" fontWeight="light" fontSize="sm">{`Modified ${formatDate(new Date(ticker.lastUpdatedDate))} by ${
              ticker.lastUpdatedByUser
            }`}</Text>
          )}
        </Stack>
      )}

      <DynamicForm<UpdateTickerForm>
        formId="update-ticker-form"
        defaultValues={defaultValues}
        fields={fields}
        onSubmit={(values) => onSubmit(values, ticker?.id)}
        title="Update ticker"
        isDisabled={isLoading}
      />

      {ticker?.changeLog && (
        <Stack spacing="1rem">
          <Text color={textColor} fontSize="sm" fontWeight={"bold"} paddingLeft="sm">
            Change Log
          </Text>
          <TickerChangeLog changeLog={ticker.changeLog} maxHeight="16rem" />
        </Stack>
      )}
    </Stack>
  );
};

const UpdateTickerPanel = (props: IPanelProps) => {
  const { onClose, isOpen, isLoading, ...rest } = props;
  const commonButtonProps = useButtonProps("sm", "primary");

  return (
    <PanelView
      isOpen={isOpen}
      onClose={onClose}
      panelTitle={`Update Company: ${rest.ticker?.name}`}
      panelFooter={
        <Box display={"flex"} justifyContent={"flex-end"} width="100%">
          <Button isLoading={isLoading} {...commonButtonProps} type="submit" form="update-ticker-form">
            Update Company
          </Button>
        </Box>
      }>
      <Wizard>
        <PanelStep>
          <Box pt="1rem" pb="4rem">
            {isOpen && rest.ticker && <UpdateTicker {...rest} />}
          </Box>
        </PanelStep>
      </Wizard>
    </PanelView>
  );
};

export { UpdateTickerPanel };
